// Generic 1-D signal helpers used by gait-event detection. Pure + testable.

export interface Peak {
  index: number;
  value: number;
  prominence: number;
}

/**
 * Find local maxima with a minimum prominence and a minimum index spacing.
 * Used to locate the per-stride extrema that mark gait events.
 *
 * @param minDistance minimum number of samples between accepted peaks
 * @param minProminence minimum height above the local baseline
 */
export function findPeaks(
  xs: number[],
  minDistance = 1,
  minProminence = 0,
): Peak[] {
  const candidates: Peak[] = [];
  for (let i = 1; i < xs.length - 1; i++) {
    if (xs[i] > xs[i - 1] && xs[i] >= xs[i + 1]) {
      candidates.push({ index: i, value: xs[i], prominence: prominenceAt(xs, i) });
    }
  }
  const filtered = candidates.filter((p) => p.prominence >= minProminence);
  filtered.sort((a, b) => b.value - a.value);

  const accepted: Peak[] = [];
  for (const p of filtered) {
    if (accepted.every((q) => Math.abs(q.index - p.index) >= minDistance)) {
      accepted.push(p);
    }
  }
  accepted.sort((a, b) => a.index - b.index);
  return accepted;
}

/** Local prominence: peak height minus the higher of the two adjacent valleys. */
function prominenceAt(xs: number[], i: number): number {
  let left = xs[i];
  for (let k = i - 1; k >= 0; k--) {
    if (xs[k] > xs[i]) break;
    left = Math.min(left, xs[k]);
  }
  let right = xs[i];
  for (let k = i + 1; k < xs.length; k++) {
    if (xs[k] > xs[i]) break;
    right = Math.min(right, xs[k]);
  }
  return xs[i] - Math.max(left, right);
}

/** Negate a series so findPeaks locates valleys (minima). */
export function negate(xs: number[]): number[] {
  return xs.map((v) => -v);
}

export function mean(xs: number[]): number {
  return xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0;
}

export function std(xs: number[]): number {
  if (xs.length < 2) return 0;
  const m = mean(xs);
  return Math.sqrt(
    xs.reduce((a, b) => a + (b - m) * (b - m), 0) / (xs.length - 1),
  );
}
